// Main App component - Simplified with clean auth flow
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { lazy, Suspense, useEffect, useLayoutEffect, useState, useRef } from 'react';
import type { ReactNode } from 'react';
import { MainLayout } from '@components/layout/MainLayout';
import { StandaloneLayout } from '@components/layout/StandaloneLayout';
import { LayoutShell } from '@components/layout/LayoutShell';
import { ProtectedAdminRoute } from '@components/admin/ProtectedAdminRoute';
import { URLTracker } from '@components/routing/URLTracker';
import { ToastProvider, ConfirmationProvider } from '@components/ui';
import ConsentBanner from '@components/ConsentBanner';
import { ROUTES } from '@config/constants';
import { getLastPath, isAllowedRedirectPath, setLastPath } from '@utils/urlTracker';

// Redirect von "/" zur zuletzt besuchten Seite (wird bei jedem Render neu aus localStorage gelesen)
function HomeRedirectTarget() {
  const defaultRoute = ROUTES.DASHBOARD;
  const last = getLastPath();
  const to = last && isAllowedRedirectPath(last) ? last : defaultRoute;
  return <Navigate to={to} replace />;
}
import { useAuthStore } from '@stores/authStore';
import { SuspendedDialog } from '@components/modals/SuspendedDialog';
import { supabase } from '@config/supabase';
import { DowngradeWarningDialog } from '@components/modals/DowngradeWarningDialog';
import { SuspensionWarningDialog } from '@components/modals/SuspensionWarningDialog';
import { OverflowBanner } from '@components/OverflowBanner';
import { SuspensionBanner } from '@components/SuspensionBanner';
import { useDowngradeWarning } from '@hooks/useDowngradeWarning';
import { useSuspensionWarning } from '@hooks/useSuspensionWarning';
import { RouteLoadingFallback } from '@components/routing/RouteLoadingFallback';

// Lazy load all route components for code splitting with retry logic
import { loadChunkWithRetry } from '@utils/chunkLoader';

const Dashboard = lazy(() => loadChunkWithRetry(() => import('@features/dashboard/Dashboard').then(m => ({ default: m.Dashboard }))));
const CloudExplorer = lazy(() => loadChunkWithRetry(() => import('@features/cloud-explorer/CloudExplorer').then(m => ({ default: m.CloudExplorer }))));
const SharingPage = lazy(() => loadChunkWithRetry(() => import('@features/sharing').then(m => ({ default: m.SharingPage }))));
const PublicBeatPackView = lazy(() => loadChunkWithRetry(() => import('@features/sharing').then(m => ({ default: m.PublicBeatPackView }))));
const PublicFileShareView = lazy(() => loadChunkWithRetry(() => import('@features/sharing').then(m => ({ default: m.PublicFileShareView }))));
const PublicFolderView = lazy(() => loadChunkWithRetry(() => import('@features/sharing').then(m => ({ default: m.PublicFolderView }))));
const VersionPage = lazy(() => loadChunkWithRetry(() => import('@features/version').then(m => ({ default: m.VersionPage }))));
const LoginPage = lazy(() => loadChunkWithRetry(() => import('@features/auth/pages/LoginPage').then(m => ({ default: m.LoginPage }))));
const VerificationPage = lazy(() => loadChunkWithRetry(() => import('@features/auth/pages/VerificationPage').then(m => ({ default: m.VerificationPage }))));
const ResetPasswordRequest = lazy(() => loadChunkWithRetry(() => import('@features/auth/ResetPasswordRequest').then(m => ({ default: m.ResetPasswordRequest }))));
const ResetPassword = lazy(() => loadChunkWithRetry(() => import('@features/auth/ResetPassword').then(m => ({ default: m.ResetPassword }))));
const SetPassword = lazy(() => loadChunkWithRetry(() => import('@features/auth/SetPassword').then(m => ({ default: m.SetPassword }))));
const EmailChangeConfirmPage = lazy(() => loadChunkWithRetry(() => import('@features/auth/EmailChangeConfirmationPage').then(m => ({ default: m.EmailChangeConfirmationPage }))));
const ErrorDenied = lazy(() => loadChunkWithRetry(() => import('@features/auth/ErrorDenied').then(m => ({ default: m.ErrorDenied }))));
const SettingsPage = lazy(() => loadChunkWithRetry(() => import('@features/settings').then(m => ({ default: m.SettingsPage }))));
const AdminDashboard = lazy(() => loadChunkWithRetry(() => import('@features/admin').then(m => ({ default: m.AdminDashboard }))));
const BillingPage = lazy(() => loadChunkWithRetry(() => import('@features/billing').then(m => ({ default: m.BillingPage }))));
const CheckoutSuccessPage = lazy(() => loadChunkWithRetry(() => import('@features/billing').then(m => ({ default: m.CheckoutSuccessPage }))));
const CheckoutCancelPage = lazy(() => loadChunkWithRetry(() => import('@features/billing').then(m => ({ default: m.CheckoutCancelPage }))));
const SupportPage = lazy(() => loadChunkWithRetry(() => import('@features/support').then(m => ({ default: m.SupportPage }))));
const YouTubeUploadPage = lazy(() => loadChunkWithRetry(() => import('@features/youtube').then(m => ({ default: m.YouTubeUploadPage }))));
const YouTubeOAuthCallback = lazy(() => loadChunkWithRetry(() => import('@features/youtube/components/YouTubeOAuthCallback').then(m => ({ default: m.YouTubeOAuthCallback }))));
const DropboxOAuthCallback = lazy(() => loadChunkWithRetry(() => import('@features/auth/DropboxOAuthCallback').then(m => ({ default: m.DropboxOAuthCallback }))));

// Shared "Coming Soon" placeholder for routes not yet implemented (single lazy chunk)
const ComingSoon = lazy(() =>
  import('@components/ComingSoonPlaceholder').then((m) => ({ default: m.ComingSoonPlaceholder }))
);
const Pricing = lazy(() => loadChunkWithRetry(() => import('@features/pricing').then(m => ({ default: m.PricingPage }))));

const withRouteFallback = (element: ReactNode) => (
  <Suspense fallback={<RouteLoadingFallback />}>
    {element}
  </Suspense>
);

// Conditional LayoutShell - hides on share pages, checkout pages, and pricing page
const LayoutShellConditional: React.FC<{ isAuthenticated: boolean }> = ({ isAuthenticated }) => {
  const location = useLocation();
  const isSharePage = location.pathname.startsWith('/share/');
  const isCheckoutPage = location.pathname.startsWith('/billing/success') || location.pathname.startsWith('/billing/cancel');
  const isPricingPage = location.pathname === '/pricing';
  const isPasswordPage =
    location.pathname === ROUTES.RESET_PASSWORD ||
    location.pathname === ROUTES.SET_PASSWORD ||
    location.pathname === ROUTES.RESET_PASSWORD_REQUEST;
  const isDropboxCallbackPage = location.pathname === ROUTES.DROPBOX_CALLBACK;
  
  // Don't render LayoutShell on standalone flows (no header/sidebar)
  if (isSharePage || isCheckoutPage || isPricingPage || isPasswordPage || isDropboxCallbackPage) {
    return null;
  }
  
  return isAuthenticated ? <LayoutShell /> : null;
};

// Inner component that has access to router context for banners and dialogs
function AppContent() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  const location = useLocation();
  const isSuspended = user?.isSuspended || false;
  const downgradeWarning = useDowngradeWarning();
  const suspensionWarning = useSuspensionWarning();
  
  // Hide banners on beatpacks/sharing page and on share pages (viewing someone else's content)
  const isBeatpacksPage = location.pathname === ROUTES.SHARING;
  const isSharePage = location.pathname.startsWith('/share/');
  
  // Only show banners/dialogs when inside MainLayout (not on share pages or standalone pages)
  const shouldShowBanners = isAuthenticated && !isSharePage && !isBeatpacksPage;
  
  return (
    <>
      <DowngradeWarningDialog
        open={shouldShowBanners && downgradeWarning.showDialog && !downgradeWarning.isLoading}
        onClose={() => {
          // Dialog will handle dismissal in database
        }}
        storageUsed={downgradeWarning.storageUsed}
        storageLimit={downgradeWarning.storageLimit}
        daysRemaining={downgradeWarning.daysRemaining}
      />
      <SuspensionWarningDialog
        open={shouldShowBanners && suspensionWarning.showDialog && !suspensionWarning.isLoading && isSuspended}
        onClose={() => {
          // Dialog can be closed by user
        }}
        storageUsed={suspensionWarning.storageUsed}
        storageLimit={suspensionWarning.storageLimit}
        daysRemaining={suspensionWarning.daysRemaining}
        suspensionStartedAt={suspensionWarning.suspensionStartedAt}
        suspensionDownloadExpiresAt={suspensionWarning.suspensionDownloadExpiresAt}
        suspensionGracePeriodDays={suspensionWarning.suspensionGracePeriodDays}
      />
      {/* Overflow and Suspension Banners - Only shown in MainLayout, not on share pages or beatpacks page */}
      {shouldShowBanners && !isSuspended && <OverflowBanner />}
      {shouldShowBanners && isSuspended && <SuspensionBanner />}
    </>
  );
}

function App() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  const isLoading = useAuthStore((s) => s.isLoading);
  const error = useAuthStore((s) => s.error);
  const initialize = useAuthStore((s) => s.initialize);
  const isSuspended = user?.isSuspended || false;
  const [showSuspendedDialog, setShowSuspendedDialog] = useState(false);
  const authInitializedRef = useRef(false);
  // Einmal ausgeblendet → nie wieder "Loading session" in dieser Tab-Session (wie Konkurrenz)
  // Wenn Auth/Store beim Start aus Cache hydratisiert ist, sofort App anzeigen
  const [showLoading, setShowLoading] = useState(() => {
    if (typeof window === 'undefined') return true;
    if (sessionStorage.getItem('fyle-app-ready') === '1') return false;
    try {
      if (useAuthStore.getState().user?.id) return false;
    } catch (_) {}
    return true;
  });

  // Set static title
  useEffect(() => {
    document.title = 'fyle';
  }, []);

  // Initialize auth on mount - only once, NEVER re-run
  useEffect(() => {
    if (!authInitializedRef.current) {
      console.log('🚀 App mounted - initializing auth...');
      authInitializedRef.current = true; // Mark as initialized immediately to prevent re-runs
      initialize().then(() => {
        console.log('✅ Auth initialized');
      }).catch((err: unknown) => {
        console.error('❌ Auth initialization failed:', err);
      });
    }
  }, [initialize]); // Only depend on initialize, not on authInitializedRef

  // Hide initial app shell loading once auth is known (avoid extra runs on unrelated user object updates)
  useEffect(() => {
    if (isAuthenticated) {
      setShowLoading(false);
      if (typeof window !== 'undefined') sessionStorage.setItem('fyle-app-ready', '1');
      return;
    }
    if (!isLoading) {
      setShowLoading(false);
      if (typeof window !== 'undefined') sessionStorage.setItem('fyle-app-ready', '1');
    }
  }, [isLoading, isAuthenticated]);

  // Prefetch Files/Folders sofort wenn User da ist (auch bei Hydration – nicht erst auf INITIAL_SESSION warten)
  useEffect(() => {
    if (!user?.id) return;
    import('@stores/prefetchAppData').then((m) => m.prefetchAppData()).catch(() => {});
  }, [user?.id]);

  // Safety: Loading spätestens nach 8s ausblenden (falls Auth hängt)
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (showLoading) {
        setShowLoading(false);
        if (typeof window !== 'undefined') sessionStorage.setItem('fyle-app-ready', '1');
      }
    }, 8000);
    return () => clearTimeout(timeout);
  }, [showLoading]);

  // URL-Tracker: Aktuelle Browser-URL sofort beim App-Start syncen, damit Reload auf derselben Seite bleibt
  useLayoutEffect(() => {
    if (showLoading || typeof window === 'undefined') return;
    const path = window.location.pathname || '/';
    const full = path + (window.location.search || '');
    if (path !== '/' && path !== '' && isAllowedRedirectPath(full)) {
      setLastPath(full);
    }
  }, [showLoading]);
  
  // Show suspended dialog only for manually suspended accounts (without download access)
  useEffect(() => {
    const checkSuspensionType = async () => {
      if (!isSuspended && error !== 'ACCOUNT_SUSPENDED') {
        setShowSuspendedDialog(false);
        return;
      }

      if (!user?.id) {
        setShowSuspendedDialog(false);
        return;
      }

      try {
        const { data: profile } = await supabase
          .from('profiles')
          .select('suspension_download_enabled')
          .eq('id', user.id)
          .single();

        // Only show dialog if account is suspended AND download is NOT enabled (manual suspension)
        const isManualSuspension = profile?.suspension_download_enabled !== true;
        setShowSuspendedDialog(isManualSuspension);
      } catch (error) {
        console.error('Error checking suspension type:', error);
        // On error, don't show dialog
        setShowSuspendedDialog(false);
      }
    };

    if (isAuthenticated && user) {
      checkSuspensionType();
    } else {
      setShowSuspendedDialog(false);
    }
  }, [isSuspended, error, user?.id, isAuthenticated]);

  const defaultRoute = ROUTES.DASHBOARD;

  return (
      <ToastProvider>
        <ConfirmationProvider>
        {/* BrowserRouter with stable key to prevent re-initialization */}
        <BrowserRouter key="main-router">
          {/* Kein Vollbild „Session wird geladen“ – wie moderne SaaS sofort App/Login anzeigen */}
          <>
          <SuspendedDialog 
            isOpen={showSuspendedDialog} 
          />
          <AppContent />
          <URLTracker />
          
          {/* Layout Shell - Header and Sidebar rendered outside Router flow */}
          {/* This prevents them from re-rendering on route changes */}
          {/* Hide LayoutShell on share pages (standalone view) */}
          <LayoutShellConditional isAuthenticated={isAuthenticated} />
          
              <Routes>
              {/* Login Route - redirect to dashboard if already authenticated */}
              <Route 
                path={ROUTES.LOGIN} 
                element={
                  isAuthenticated ? (
                    (() => {
                      const searchParams = new URLSearchParams(window.location.search);
                      const redirectPath = searchParams.get('redirect');
                      const decoded = redirectPath ? decodeURIComponent(redirectPath) : '';
                      const authPaths = ['/login', '/signup', '/reset-password', '/set-password', '/verify-email'];
                      const target = decoded && decoded.startsWith('/') && !authPaths.some(p => decoded === p || decoded.startsWith(p + '?'))
                        ? decoded
                        : defaultRoute;
                      return <Navigate to={target} replace />;
                    })()
                  ) : (
                    withRouteFallback(<LoginPage />)
                  )
                } 
              />

              {/* Password Reset Routes */}
              <Route 
                path={ROUTES.RESET_PASSWORD_REQUEST} 
                element={
                  isAuthenticated ? (
                    <Navigate to={defaultRoute} replace />
                  ) : (
                    withRouteFallback(<ResetPasswordRequest />)
                  )
                } 
              />
                <Route 
                  path={ROUTES.RESET_PASSWORD} 
                  element={withRouteFallback(<ResetPassword />)} 
                />
                <Route 
                  path={ROUTES.SET_PASSWORD} 
                  element={withRouteFallback(<SetPassword />)} 
                />
                
              {/* Email Verification Route */}
              <Route 
                path={ROUTES.EMAIL_VERIFICATION} 
                element={
                  isAuthenticated ? (
                    <Navigate to={defaultRoute} replace />
                  ) : (
                    withRouteFallback(<VerificationPage />)
                  )
                } 
              />

              {/* Email Change Confirmation Route */}
                <Route 
                  path={ROUTES.EMAIL_CHANGE_CONFIRM} 
                element={withRouteFallback(<EmailChangeConfirmPage />)} 
                />
                
              {/* Error Denied Route */}
              <Route 
                path={ROUTES.ERROR_DENIED} 
                element={withRouteFallback(<ErrorDenied />)} 
              />

              {/* Standalone pages (no sidebar/header) */}
              <Route element={<StandaloneLayout />}>
                <Route 
                  path={ROUTES.BILLING_SUCCESS} 
                  element={withRouteFallback(<CheckoutSuccessPage />)} 
                />
                <Route 
                  path={ROUTES.BILLING_CANCEL} 
                  element={withRouteFallback(<CheckoutCancelPage />)} 
                />
                <Route 
                  path="/pricing" 
                  element={withRouteFallback(<Pricing />)} 
                />
              </Route>

              {/* Public Share Views - NO LOGIN REQUIRED - Outside Layout */}
              <Route 
                path="/share/:packId" 
                element={withRouteFallback(<PublicBeatPackView />)} 
              />
              <Route 
                path="/share/file/:token" 
                element={withRouteFallback(<PublicFileShareView />)} 
              />
              <Route 
                path="/share/folder/:token" 
                element={withRouteFallback(<PublicFolderView />)} 
              />

              {/* Dropbox OAuth Callback – öffentlich, wird sofort gerendert (kein Auth nötig) */}
              <Route 
                path={ROUTES.DROPBOX_CALLBACK} 
                element={withRouteFallback(<DropboxOAuthCallback />)} 
              />

                          {/* Protected Routes */}
              {isAuthenticated ? (
                <Route element={<MainLayout />}>
                          <Route 
                            path={ROUTES.HOME} 
                            element={<HomeRedirectTarget />} 
                          />
                          <Route 
                            path={ROUTES.DASHBOARD} 
                            element={withRouteFallback(<Dashboard />)} 
                          />
                          <Route 
                            path={ROUTES.CLOUD_EXPLORER} 
                            element={withRouteFallback(<CloudExplorer />)} 
                          />
                          <Route 
                            path={ROUTES.SHARING} 
                            element={withRouteFallback(<SharingPage />)} 
                          />
                          <Route 
                            path={ROUTES.VERSION} 
                            element={withRouteFallback(<VersionPage />)} 
                          />
                          <Route 
                            path={ROUTES.TAG_EDITOR} 
                            element={withRouteFallback(<ComingSoon title="Tag Editor" />)} 
                          />
                          <Route 
                            path={ROUTES.NAME_PRESETS} 
                            element={withRouteFallback(<ComingSoon title="Name Presets" />)} 
                          />
                          <Route 
                            path={ROUTES.TEAM} 
                            element={withRouteFallback(<ComingSoon title="Team & Clearance" />)} 
                          />
                          <Route 
                            path={ROUTES.BILLING} 
                            element={withRouteFallback(<BillingPage />)} 
                          />
                          <Route 
                            path={ROUTES.SUPPORT} 
                            element={withRouteFallback(<SupportPage />)} 
                          />
                          <Route 
                            path={ROUTES.YOUTUBE} 
                            element={withRouteFallback(<YouTubeUploadPage />)} 
                          />
                          <Route 
                            path="/youtube/oauth/callback" 
                            element={withRouteFallback(<YouTubeOAuthCallback />)} 
                          />
                          <Route 
                            path={ROUTES.SETTINGS} 
                            element={withRouteFallback(<SettingsPage />)} 
                          />
                          <Route 
                            path="/admin" 
                            element={
                              <ProtectedAdminRoute>
                                {withRouteFallback(<AdminDashboard />)}
                              </ProtectedAdminRoute>
                            } 
                          />
                          <Route
                            path={ROUTES.UPLOAD}
                            element={<Navigate to={ROUTES.CLOUD_EXPLORER} replace />}
                          />
                </Route>
              ) : isLoading ? (
                // Session still resolving: keep URL, show neutral loader — do not bounce to /login (fixes tab-refocus flicker).
                <Route path="*" element={<RouteLoadingFallback />} />
              ) : (
                // Redirect to login if not authenticated (nur wenn Auth definitiv bekannt)
                        <Route 
                          path="*" 
                          element={
                            <Navigate 
                              to={`${ROUTES.LOGIN}?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`} 
                              replace 
                            />
                  }
                />
              )}
              </Routes>
          </>
        </BrowserRouter>
          <ConsentBanner showManageButton={false} />
        </ConfirmationProvider>
      </ToastProvider>
  );
}

export default App;
